import React, { memo } from "react";
import type { MachineEvent } from "@/machines/types/event";
import type { EventSegment } from "../hooks/useTimelineEvents";
import { getEventUniqueId, getChangeIndicatorColor } from "../utils/timelineHelpers";

interface TimelineEventRectProps {
  seg: EventSegment;
  displayStartX: number;
  displayEndX: number;
  displayWidth: number;
  rowHeight: number;
  barHeight: number;
  fillColor: string;
  strokeColor: string;
  isEditMode: boolean;
  isTruckCycle: boolean;
  isSelectedForEdit: boolean;
  isEditableInEditMode: boolean;

  // Overlap preview
  isOverlapped: boolean;

  // Event handlers
  onEventEnter: (e: React.MouseEvent<SVGRectElement>, seg: EventSegment) => void;
  onEventLeave: () => void;
  onClick: (event: MachineEvent) => void;
  /** Non-edit-mode click on a segment — opens the event's detail (e.g. the map's on-map card). */
  onEventOpen?: (event: MachineEvent, isTruckCycle: boolean) => void;
  onMouseDown: (event: MachineEvent, clientX: number) => void;
  onDoubleClick: (seg: EventSegment, e: React.MouseEvent<SVGRectElement>) => void;

  // Edit mode
  hasEventChanges: (id: string) => { type: string } | null;

  // Resize handles
  onResizeStart: (event: MachineEvent, handle: "start" | "end", clientX: number) => void;
}

const TimelineEventRectComponent: React.FC<TimelineEventRectProps> = ({
  seg,
  displayStartX,
  displayEndX,
  displayWidth,
  rowHeight,
  barHeight,
  fillColor,
  strokeColor,
  isEditMode,
  isTruckCycle,
  isSelectedForEdit,
  isEditableInEditMode,
  isOverlapped,
  onEventEnter,
  onEventLeave,
  onClick,
  onEventOpen,
  onMouseDown,
  onDoubleClick,
  hasEventChanges,
  onResizeStart,
}) => {
  return (
    <g className={seg.isNew ? "timeline-seg timeline-event-pop" : "timeline-seg"}>
      <rect
        x={displayStartX}
        y={(rowHeight - barHeight) / 2}
        width={displayWidth}
        height={barHeight}
        fill={isOverlapped ? "rgba(239, 68, 68, 0.4)" : fillColor}
        stroke={isOverlapped ? "#ef4444" : isSelectedForEdit ? "#3b82f6" : strokeColor}
        strokeWidth={isOverlapped ? 2 : isSelectedForEdit ? 2 : 1}
        strokeDasharray={isOverlapped ? "4,2" : undefined}
        opacity={isOverlapped ? 0.5 : 1}
        rx={2}
        onMouseEnter={(e) => onEventEnter(e, seg)}
        onMouseLeave={onEventLeave}
        onClick={(e) => {
          // In edit mode, select the event for editing and stop propagation
          if (isEditMode && isTruckCycle && seg.event) {
            e.stopPropagation();
            onClick(seg.event);
          }
          // In non-edit mode, open the event's detail — and let the click
          // bubble to the SVG for cursor freeze (no stopPropagation).
          if (!isEditMode && seg.event) {
            onEventOpen?.(seg.event, isTruckCycle);
          }
        }}
        onMouseDown={(e) => {
          if (isEditableInEditMode && isSelectedForEdit && seg.event) {
            e.stopPropagation();
            onMouseDown(seg.event, e.clientX);
          }
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          // Only handle double-click if not in edit mode
          if (!isEditMode) {
            onDoubleClick(seg, e);
          }
        }}
        style={{ cursor: isEditableInEditMode ? 'pointer' : 'default' }}
      />

      {/* Change indicator - show small dot if event has pending changes */}
      {isEditMode && seg.event && (() => {
        const uniqueId = getEventUniqueId(seg.event);
        const change = hasEventChanges(uniqueId);
        if (!change) return null;

        const indicatorColor = getChangeIndicatorColor(change.type);
        const indicatorSize = 8;

        return (
          <circle
            cx={displayStartX + 6}
            cy={(rowHeight - barHeight) / 2 + 6}
            r={indicatorSize / 2}
            fill={indicatorColor}
            stroke="#ffffff"
            strokeWidth={1.5}
          />
        );
      })()}

      {/* Resize handles - only show for truck_cycles in edit mode */}
      {isEditableInEditMode && isSelectedForEdit && seg.event && (
        <>
          {/* Left resize handle */}
          <rect
            x={displayStartX - 3}
            y={(rowHeight - barHeight) / 2}
            width={6}
            height={barHeight}
            fill="#3b82f6"
            stroke="#ffffff"
            strokeWidth={1}
            rx={1}
            style={{ cursor: 'ew-resize' }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(seg.event!, 'start', e.clientX);
            }}
          />

          {/* Right resize handle */}
          <rect
            x={displayEndX - 3}
            y={(rowHeight - barHeight) / 2}
            width={6}
            height={barHeight}
            fill="#3b82f6"
            stroke="#ffffff"
            strokeWidth={1}
            rx={1}
            style={{ cursor: 'ew-resize' }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(seg.event!, 'end', e.clientX);
            }}
          />
        </>
      )}
    </g>
  );
};

// Custom equality check - only re-render when visual properties change
const arePropsEqual = (
  prevProps: TimelineEventRectProps,
  nextProps: TimelineEventRectProps
): boolean => {
  // Check position and size (these affect visual output)
  if (
    prevProps.displayStartX !== nextProps.displayStartX ||
    prevProps.displayEndX !== nextProps.displayEndX ||
    prevProps.displayWidth !== nextProps.displayWidth ||
    prevProps.rowHeight !== nextProps.rowHeight ||
    prevProps.barHeight !== nextProps.barHeight
  ) {
    return false;
  }

  // Check visual state
  if (
    prevProps.fillColor !== nextProps.fillColor ||
    prevProps.strokeColor !== nextProps.strokeColor ||
    prevProps.isEditMode !== nextProps.isEditMode ||
    prevProps.isTruckCycle !== nextProps.isTruckCycle ||
    prevProps.isSelectedForEdit !== nextProps.isSelectedForEdit ||
    prevProps.isEditableInEditMode !== nextProps.isEditableInEditMode ||
    prevProps.isOverlapped !== nextProps.isOverlapped
  ) {
    return false;
  }

  // Check segment identity (not deep equality)
  if (prevProps.seg !== nextProps.seg) {
    // Only re-render if the event ID or its live pop-in flag changed
    const prevId = prevProps.seg.event?.id || prevProps.seg.event?.truckId;
    const nextId = nextProps.seg.event?.id || nextProps.seg.event?.truckId;
    if (prevId !== nextId) {
      return false;
    }
    if (prevProps.seg.isNew !== nextProps.seg.isNew) {
      return false;
    }
  }

  // Presence, not identity. `MapTimelineRow` swaps this between the handler and
  // `undefined` when a row activates (MapTimelineRow.tsx:266, and again at
  // map-preview-page.tsx:1239), and nothing else compared here changes with it —
  // under a frozen review domain `min`/`max` and `track` all hold still, so the
  // memo kept the `undefined` captured while the row was inactive and clicking a
  // segment did nothing, permanently. Identity would be the wrong test: sibling
  // callbacks are declared inline in the parent and get a new identity every
  // render, so comparing identity would defeat the memo outright.
  if (!prevProps.onEventOpen !== !nextProps.onEventOpen) return false;

  // Every OTHER callback is assumed stable. That is not literally true — the
  // parent declares `onEventEnter`/`onEventLeave` inline — but those only ever
  // fire from a fresh pointer event, so a stale identity costs nothing.
  return true;
};

export const TimelineEventRect = memo(TimelineEventRectComponent, arePropsEqual);
